import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FilterState {
  accessLevel: string
  tags: string[]
  authors: string[]
}

interface FilterPanelProps {
  filterState: FilterState
  onFilterChange: (filters: FilterState) => void
  availableTags: string[]
  availableAuthors: string[]
  canFilterByAccessLevel: boolean
  totalCount: number
  filteredCount: number
  onClearAll: () => void
  className?: string
}

interface CollapsibleSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function CollapsibleSection({ title, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 px-4 min-h-[44px] hover:bg-muted/50 transition-colors text-left"
      >
        <span className="font-medium text-sm">{title}</span>
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform flex-shrink-0",
            isOpen && "rotate-180"
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function FilterPanel({
  filterState,
  onFilterChange,
  availableTags,
  availableAuthors,
  canFilterByAccessLevel,
  totalCount,
  filteredCount,
  onClearAll,
  className
}: FilterPanelProps) {
  const accessLevels = [
    { value: 'all', label: 'All Levels' },
    { value: 'demo', label: 'Demo' },
    { value: 'company', label: 'Company' },
    { value: 'admin', label: 'Admin' }
  ]

  const hasActiveFilters =
    filterState.accessLevel !== 'all' ||
    filterState.tags.length > 0 ||
    filterState.authors.length > 0

  const handleAccessLevelChange = (level: string) => {
    onFilterChange({ ...filterState, accessLevel: level })
  }

  const handleTagToggle = (tag: string) => {
    const newTags = filterState.tags.includes(tag)
      ? filterState.tags.filter(t => t !== tag)
      : [...filterState.tags, tag]
    onFilterChange({ ...filterState, tags: newTags })
  }

  const handleAuthorToggle = (author: string) => {
    const newAuthors = filterState.authors.includes(author)
      ? filterState.authors.filter(a => a !== author)
      : [...filterState.authors, author]
    onFilterChange({ ...filterState, authors: newAuthors })
  }

  return (
    <div className={cn("bg-background border rounded-lg overflow-hidden", className)}>
      {/* Header with Clear All */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h3 className="font-semibold">Filters</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Showing {filteredCount} of {totalCount} presentations
          </p>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>

      {/* Filter Sections */}
      <div className="max-h-[400px] overflow-y-auto">
        {/* Access Level Filter */}
        {canFilterByAccessLevel && (
          <CollapsibleSection title="Access Level" defaultOpen={true}>
            <div className="space-y-1">
              {accessLevels.map((level) => (
                <button
                  key={level.value}
                  onClick={() => handleAccessLevelChange(level.value)}
                  className={cn(
                    "w-full text-left px-3 py-3 min-h-[44px] rounded-md text-sm transition-colors",
                    filterState.accessLevel === level.value
                      ? "bg-primary text-primary-foreground font-medium"
                      : "hover:bg-muted"
                  )}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Tags Filter */}
        {availableTags.length > 0 && (
          <CollapsibleSection title="Tags">
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => {
                const isSelected = filterState.tags.includes(tag)
                return (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={cn(
                      "px-3 py-2 min-h-[44px] rounded-full text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    {tag}
                    {isSelected && (
                      <X className="inline w-3 h-3 ml-1" />
                    )}
                  </button>
                )
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Authors Filter */}
        {availableAuthors.length > 0 && (
          <CollapsibleSection title="Authors" defaultOpen={false}>
            <div className="space-y-1">
              {availableAuthors.map((author) => {
                const isSelected = filterState.authors.includes(author)
                return (
                  <button
                    key={author}
                    onClick={() => handleAuthorToggle(author)}
                    className={cn(
                      "w-full text-left px-3 py-3 min-h-[44px] rounded-md text-sm transition-colors flex items-center justify-between",
                      isSelected
                        ? "bg-primary text-primary-foreground font-medium"
                        : "hover:bg-muted"
                    )}
                  >
                    <span>{author}</span>
                    {isSelected && <X className="w-3 h-3" />}
                  </button>
                )
              })}
            </div>
          </CollapsibleSection>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="p-4 border-t bg-muted/30">
          <div className="flex flex-wrap gap-2">
            {filterState.accessLevel !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                Access: {filterState.accessLevel}
                <button
                  onClick={() => handleAccessLevelChange('all')}
                  className="hover:bg-primary/20 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filterState.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
              >
                {tag}
                <button
                  onClick={() => handleTagToggle(tag)}
                  className="hover:bg-primary/20 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {filterState.authors.map((author) => (
              <span
                key={author}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
              >
                {author}
                <button
                  onClick={() => handleAuthorToggle(author)}
                  className="hover:bg-primary/20 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
